/**
 * Mobile Inquiry Inbox — /app?p=inbox
 * Every client request (web form, inbound SMS, manual) in one pipeline.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const T = {
  bg: '#FAFAFA', card: '#FFFFFF', border: '#E5E5E5',
  red: '#CC0000', gold: '#A8862F', green: '#15803D', amber: '#B45309',
  text: '#0A0A0A', light: '#525252', dim: '#A3A3A3',
};
const STATUSES = ['new', 'quoted', 'booked', 'completed', 'lost'];
const STATUS_COLORS = { new: T.red, quoted: T.amber, booked: '#1D4ED8', completed: T.green, lost: T.dim };
const SOURCE_ICONS = { sms: '💬', web_form: '🌐', intake: '📋', manual: '✍️', quote: '♦', phone: '📞' };

export function MobileInbox({ onOpenClient }) {
  const [inquiries, setInquiries] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ops/inquiries${filter !== 'all' ? `?status=${filter}` : ''}`);
      const d = await r.json();
      setInquiries(d.inquiries || []);
      setCounts(d.counts || {});
    } catch { toast.error('Failed to load inbox'); }
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await fetch(`${API}/api/ops/inquiries/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    load();
  };

  const sendReply = async (q) => {
    if (!replyText.trim()) return;
    const r = await fetch(`${API}/api/command-center/quo/send-sms`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: q.client_phone, message: replyText.trim() }) });
    const d = await r.json();
    if (d.success) { toast.success('SMS sent'); setReplyId(null); setReplyText(''); }
    else toast.error('SMS failed');
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h`;
    return `${Math.floor(mins / 1440)}d`;
  };

  return (
    <div data-testid="mobile-inbox">
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Inquiry Inbox</p>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, paddingBottom: 2 }}>
        {['all', ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            flexShrink: 0, padding: '6px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
            border: `1px solid ${filter === s ? T.text : T.border}`,
            background: filter === s ? T.text : T.card, color: filter === s ? '#FFF' : T.light, cursor: 'pointer',
          }} data-testid={`minbox-filter-${s}`}>
            {s}{s !== 'all' && counts[s] > 0 ? ` ${counts[s]}` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: T.dim, fontSize: 12, padding: 20 }}>Loading…</p>
      ) : inquiries.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', background: T.card, border: `1px solid ${T.border}` }} data-testid="minbox-empty">
          <p style={{ fontSize: 12, color: T.dim, margin: 0 }}>No inquiries. Web forms, texts & manual entries land here.</p>
        </div>
      ) : inquiries.map(q => (
        <div key={q.inquiry_id} style={{ background: T.card, border: `1px solid ${T.border}`, padding: 12, marginBottom: 8 }} data-testid={`minbox-${q.inquiry_id}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <button onClick={() => onOpenClient && onOpenClient((q.client_phone || '').replace(/\D/g, '').slice(-10) || (q.client_email || '').toLowerCase() || (q.client_name || '').toLowerCase())} style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 800, margin: 0, color: T.text }}>{SOURCE_ICONS[q.source] || '•'} {q.client_name || q.client_phone || 'Unknown'}</p>
              {(q.service_type || q.event_date) && <p style={{ fontSize: 10, color: T.light, margin: '2px 0 0' }}>{q.service_type}{q.event_date ? ` · ${q.event_date}` : ''}</p>}
            </button>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: STATUS_COLORS[q.status] || T.dim }}>{q.status}</span>
              <p style={{ fontSize: 9, color: T.dim, margin: '2px 0 0' }}>{timeAgo(q.updated_at)}</p>
            </div>
          </div>
          {(q.messages || []).slice(-1).map((m, i) => (
            <p key={i} style={{ fontSize: 11, color: T.light, margin: '6px 0 0', padding: '4px 8px', background: T.bg, borderLeft: `2px solid ${T.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.text}</p>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <select value={q.status} onChange={e => setStatus(q.inquiry_id, e.target.value)} style={{ flex: 1, border: `1px solid ${T.border}`, background: T.card, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '6px' }} data-testid={`minbox-status-${q.inquiry_id}`}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {q.client_phone && (
              <button onClick={() => { setReplyId(replyId === q.inquiry_id ? null : q.inquiry_id); setReplyText(''); }} style={{ border: `1px solid ${T.border}`, background: T.card, color: T.red, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '6px 12px', cursor: 'pointer' }} data-testid={`minbox-reply-${q.inquiry_id}`}>
                💬 Reply
              </button>
            )}
          </div>
          {replyId === q.inquiry_id && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={`SMS to ${q.client_phone}`} style={{ flex: 1, border: `1px solid ${T.border}`, padding: '8px', fontSize: 12 }} data-testid={`minbox-reply-input-${q.inquiry_id}`} />
              <button onClick={() => sendReply(q)} disabled={!replyText.trim()} style={{ background: T.red, color: '#FFF', border: 0, padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }} data-testid={`minbox-send-${q.inquiry_id}`}>Send</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
